// client/src/pages/ApproveRequests.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiJson } from '../lib/api';
import '../App.css';
import logo from '../assets/chitconnect-logo-blue.png';
import ReputationBadge from '../components/ReputationBadge';

const toArray = (data) =>
  Array.isArray(data) ? data
  : Array.isArray(data?.pending) ? data.pending
  : Array.isArray(data?.rows) ? data.rows
  : Array.isArray(data?.members) ? data.members
  : [];

export default function ApproveRequests() {
  const { groupId } = useParams();
  const navigate = useNavigate();

  const [pendingMembers, setPendingMembers] = useState([]);
  const [groupName, setGroupName] = useState('');
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [approvedCount, setApprovedCount] = useState(1);

  // organiser object is optional; kept if other UI needs it later
  const organiser = JSON.parse(sessionStorage.getItem('user') || 'null');

  useEffect(() => {
    fetchPendingRequests();
    fetchGroupName();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const fetchGroupName = async () => {
    try {
      const res = await apiJson(`/api/groups/${groupId}`);
      if (res.ok) setGroupName(res.data?.name || '');
      else console.warn('⚠️ Could not fetch group name');
    } catch {
      console.warn('⚠️ Could not fetch group name');
    }
  };

  const fetchPendingRequests = async () => {
    try {
      const res = await apiJson(`/api/groups/${groupId}/pending-requests`);
      setPendingMembers(res.ok ? toArray(res.data) : []);
    } catch (err) {
      console.error('❌ Failed to fetch pending members:', err);
      setPendingMembers([]);
    }
    setLoading(false);
  };

  const showToast = (message) => {
    setToast(message);
    setTimeout(() => setToast(''), 3000);
  };

  const confirmApprove = (user) => {
    setSelectedUser(user);
    setApprovedCount(user.requested_count);
    setShowModal(true);
  };

  const approveMember = async () => {
    if (!selectedUser || approvedCount < 1) return;
    try {
      const res = await apiJson(`/api/groups/${groupId}/approve-member`, {
        method: 'POST',
        body: JSON.stringify({
          user_id: selectedUser.user_id,
          approved_count: approvedCount,
        }),
      });

      if (res.ok && (res.data?.success ?? true)) {
        showToast(`✅ Approved ${approvedCount} memberships for ${selectedUser.given_name}`);
        setShowModal(false);
        setSelectedUser(null);
        fetchPendingRequests();
      } else {
        showToast(res.data?.error || 'Failed to approve member');
      }
    } catch {
      showToast('❌ Server error while approving');
    }
  };

  const rejectMember = async (userId) => {
    try {
      const res = await apiJson(`/api/groups/${groupId}/reject-member/${userId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        showToast('❌ Member request rejected');
        fetchPendingRequests();
      } else {
        showToast(res.data?.error || 'Error rejecting member');
      }
    } catch {
      showToast('Error rejecting member');
    }
  };

  useEffect(() => {
    if (!loading && pendingMembers.length === 0) {
      setTimeout(() => navigate('/groups'), 2000);
    }
  }, [loading, pendingMembers, navigate]);

  return (
    <div className="register-page">
      <img src={logo} alt="ChitConnect" className="register-logo" />
      <h2>Approve Members - {groupName || `Group #${groupId}`}</h2>

      {toast && <div className="toast">{toast}</div>}

      <div className="register-form">
        {loading ? (
          <p>Loading...</p>
        ) : (Array.isArray(pendingMembers) && pendingMembers.length > 0) ? (
          <div className="table-wrapper">
            <table className="styled-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Requested</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(Array.isArray(pendingMembers) ? pendingMembers : []).map((member) => (
                  <tr key={member.user_id}>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span>{member.given_name} {member.surname}</span>
                        <ReputationBadge userId={member.user_id} className="rep-badge" />
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td>{member.mobile}</td>
                    <td>{member.requested_count}</td>
                    <td>
                      <button className="action-button small" onClick={() => confirmApprove(member)}>
                        Approve
                      </button>
                      <button className="action-button small danger" onClick={() => rejectMember(member.user_id)}>
                        Reject
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p>No pending members found. Redirecting...</p>
        )}
      </div>

      {showModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Confirm Approval</h3>
            <p>
              Approve <strong>{selectedUser.given_name} {selectedUser.surname}</strong> for how many memberships?
            </p>
            <div style={{ marginBottom: 8 }}>
              <ReputationBadge userId={selectedUser.user_id} className="rep-badge" />
            </div>
            <input
              type="number"
              value={approvedCount}
              onChange={(e) =>
                setApprovedCount(
                  Math.max(1, Math.min(selectedUser.requested_count, parseInt(e.target.value, 10) || 1))
                )
              }
              min="1"
              max={selectedUser.requested_count}
              className="input-control"
              style={{ width: '80px', marginBottom: '10px' }}
            />
            <div className="modal-actions">
              <button className="action-button" onClick={approveMember}>Approve</button>
              <button className="action-button danger" onClick={() => setShowModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
